import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getShipmentDetails,
  updateShipment,
  deleteShipment,
  getShipmentEvents,
  getShipmentDocuments,
  searchPorts,
  searchAirports,
  searchHsCodes,
} from '@wove/api'
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Badge,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@wove/web-ui'
import { ConsolidatedShipmentDetailsView } from '@wove/document-extraction'
import { format } from 'date-fns'
import {
  ArrowLeft,
  Edit,
  Trash2,
  FileText,
  Package,
  Clock,
  MapPin,
  AlertCircle,
  Check,
  X,
  Pencil,
} from 'lucide-react'
import { LocationType, ShipmentType, ShipmentTypeLabels, isOceanFreight } from '@wove/common'
import DocumentsTab from './components/DocumentsTab'
import EventsTab from './components/EventsTab'

const ShipmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('details')
  const [isEditingHeader, setIsEditingHeader] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [editedType, setEditedType] = useState('')

  const { data: shipmentData, isLoading: shipmentLoading } = useQuery({
    queryKey: ['shipment', id, 'details'],
    queryFn: () => getShipmentDetails(id!),
    enabled: !!id,
  })

  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['shipment', id, 'documents'],
    queryFn: () => getShipmentDocuments(id!),
    enabled: !!id,
  })

  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['shipment', id, 'events'],
    queryFn: () => getShipmentEvents(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (data: any) => updateShipment(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipment', id] })
      setIsEditingHeader(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteShipment(id!),
    onSuccess: () => {
      navigate('/shipments')
    },
  })

  const handleDelete = () => {
    deleteMutation.mutate()
  }

  const handleSearchHsCodes = async (query: string) => {
    const response = await searchHsCodes(query, 50)
    return response.results || []
  }

  const handleLoadShipmentDetails = async (id: string): Promise<any> => {
    const response = await getShipmentDetails(id)
    // Transform the data to match the expected format
    const { shipment, items, containers } = response.data
    return {
      shipment: {
        ...shipment,
        referenceNumbers: Object.entries(shipment.numbers || {}).map(([type, number]) => ({
          type,
          displayNumber: number,
          cleanedNumber: number,
        })),
      },
      items: items || [],
      containers: containers || [],
    }
  }

  const handleSaveShipmentDetails = async (shipmentId: string, details: any): Promise<void> => {
    // Transform the data back to the API format
    const result = await updateShipment(shipmentId, details)
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ['shipment', shipmentId] })
    }
  }

  // Set initial values when shipment data loads
  React.useEffect(() => {
    if (shipmentData?.data?.shipment) {
      setEditedName(shipmentData.data.shipment.name || shipmentData.data.shipment.commodity || '')
      setEditedType(shipmentData.data.shipment.shipmentType || '')
    }
  }, [shipmentData])

  if (shipmentLoading) {
    return (
      <div className="flex justify-center items-center h-full py-16">
        <Spinner />
      </div>
    )
  }

  if (!shipmentData?.data) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="text-center py-8">
            <AlertCircle className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-gray-500">Shipment not found</p>
            <Button variant="link" onClick={() => navigate('/shipments')} className="mt-4">
              Back to shipments
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const shipment = shipmentData.data.shipment

  const handleSaveHeader = () => {
    updateMutation.mutate({
      name: editedName || undefined,
      shipmentType: editedType || undefined,
    })
  }

  const handleCancelEdit = () => {
    setEditedName(shipment.name || shipment.commodity || '')
    setEditedType(shipment.shipmentType || '')
    setIsEditingHeader(false)
  }

  const getStatusBadge = (status: string) => {
    const statusVariants: Record<string, 'warning' | 'info' | 'success' | 'error' | 'secondary'> = {
      pending: 'warning',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'error',
    }

    return (
      <Badge variant={statusVariants[status] || 'secondary'}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    )
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/shipments')}
            leftIcon={<ArrowLeft size={16} />}
          >
            Back
          </Button>
          {isEditingHeader ? (
            <div className="flex items-end gap-3">
              <div>
                <label className="text-xs text-gray-500">Name</label>
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Shipment name"
                  className="w-48"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Type</label>
                <Select value={editedType} onValueChange={setEditedType}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ShipmentTypeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-1">
                <Button size="sm" onClick={handleSaveHeader} loading={updateMutation.isPending}>
                  <Check size={16} />
                </Button>
                <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                  <X size={16} />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">
                {shipment.name || `Shipment #${shipment.id.slice(-8)}`}
              </h1>
              <Button variant="ghost" size="sm" onClick={() => setIsEditingHeader(true)}>
                <Pencil size={16} />
              </Button>
              {getStatusBadge(shipment.status)}
            </div>
          )}
        </div>
        {!isEditingHeader && (
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              leftIcon={<Trash2 size={16} />}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="text-gray-400" size={20} />
              <div>
                <p className="text-sm text-gray-500">Type</p>
                <p className="font-medium">
                  {shipment.shipmentType
                    ? ShipmentTypeLabels[shipment.shipmentType as ShipmentType] ||
                      shipment.shipmentType
                    : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="text-gray-400" size={20} />
              <div>
                <p className="text-sm text-gray-500">Documents</p>
                <p className="font-medium">{documentsData?.data.length || 0} files</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <MapPin className="text-gray-400" size={20} />
              <div>
                <p className="text-sm text-gray-500">Route</p>
                <p className="font-medium text-sm">
                  {(() => {
                    const getLocationDisplay = (location: any) => {
                      if (!location) return 'N/A'
                      if (location.type === LocationType.PORT) return location.portCode
                      if (location.type === LocationType.AIRPORT) return location.airportCode
                      if (location.type === LocationType.DOOR) return location.city || 'Address'
                      // Legacy support for old format
                      if (location.portCode) return location.portCode
                      return 'N/A'
                    }

                    const from = isOceanFreight(shipment.shipmentType)
                      ? getLocationDisplay(shipment.portOfLoading)
                      : getLocationDisplay(shipment.placeOfReceiptPort)

                    const to = isOceanFreight(shipment.shipmentType)
                      ? getLocationDisplay(shipment.portOfDischarge)
                      : getLocationDisplay(shipment.placeOfDeliveryPort)

                    return `${from} → ${to}`
                  })()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="text-gray-400" size={20} />
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium text-sm">
                  {format(new Date(shipment.createdAt), 'MMM d, yyyy')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="details">
            <TabsList className="w-full justify-start border-b rounded-none">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="documents">
                Documents {documentsData && `(${documentsData.data.length})`}
              </TabsTrigger>
              <TabsTrigger value="events">
                Events {eventsData && `(${eventsData.data.length})`}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="p-6">
              <ConsolidatedShipmentDetailsView
                shipmentId={id!}
                onLoad={handleLoadShipmentDetails}
                onSave={handleSaveShipmentDetails}
                onSearchPorts={searchPorts}
                onSearchAirports={searchAirports}
                onSearchHsCodes={handleSearchHsCodes}
                showSaveButton={true}
                showCloseButton={false}
              />
            </TabsContent>

            <TabsContent value="documents" className="p-6">
              <DocumentsTab
                shipmentId={id!}
                documents={(documentsData?.data || []) as any}
                isLoading={documentsLoading}
              />
            </TabsContent>

            <TabsContent value="events" className="p-6">
              <EventsTab events={eventsData?.data || []} isLoading={eventsLoading} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Shipment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to delete this shipment? This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} loading={deleteMutation.isPending}>
              Delete Shipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ShipmentDetail
